/**
 * Source / licence attribution for policy templates adapted from third-party
 * open-licensed material (today: JupiterOne security-policy-templates, CC BY-SA 4.0).
 *
 * WHY THIS EXISTS
 * CC BY-SA 4.0 requires that anyone who sees an adapted work can tell who made
 * the original, where it came from, under what licence it is offered, and that
 * it was modified. The same text is also written into the template's own
 * contentHtml (so it travels into generated policies, PDF exports and Trust
 * Centre pages). This component is the in-product surface for the same facts:
 * a compact badge on template cards and a fuller panel in the editor.
 *
 * PROPS, NOT A PRISMA TYPE
 * It takes plain optional props so it compiles against the CURRENT schema and
 * renders nothing for house templates (no `license`). When PolicyTemplate gains
 * `sourceUrl`, `sourceAttribution`, `license` and `modifiedFromSource`, the API
 * returns them and callers just spread them in:
 *
 *   <LicenseAttribution variant="badge" {...template} />
 *
 * SAFETY
 * `sourceUrl` comes from the database. Only https:// URLs are ever rendered as
 * links, and always with rel="noopener noreferrer".
 */

import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn';

/** English defaults; keys are the i18n keys. List them to the PM, do not edit the locale JSON here. */
export const LICENSE_ATTRIBUTION_STRINGS = {
  'policies.license.sourceLabel': 'Source',
  'policies.license.adapted': 'adapted',
  'policies.license.badgeTitle': 'Adapted from {{creator}} under {{license}}. Modified from the original.',
  'policies.license.badgeTitleUnmodified': 'From {{creator}} under {{license}}.',
  'policies.license.heading': 'Source and licence',
  'policies.license.adaptedFrom': 'Adapted from {{source}}.',
  'policies.license.modified': 'This version has been modified from the original.',
  'policies.license.licensedUnder': 'Licensed under {{license}}.',
  'policies.license.shareAlike':
    'If you share this document or an adaptation of it, you must credit the source, indicate your changes, and license your version under {{license}} or a compatible licence. Documents you keep internal are not affected.',
  'policies.license.viewSource': 'View original',
  'policies.license.viewLicense': 'View licence',
  'policies.license.noWarranty': 'Provided without warranty. Not endorsed by the original author.',
  /** Shown by the editor when a save put the source notice back (ShareAlike guard). */
  'policies.license.restored':
    'This policy is adapted from material licensed under {{license}}, so its “Source and licence” notice has been kept at the end of the document.',
} as const;

type Key = keyof typeof LICENSE_ATTRIBUTION_STRINGS;

interface KnownLicense {
  short: string;
  url: string;
}

/** SPDX id -> display name and canonical URL. Unknown ids fall back to the raw id with no link. */
const LICENSES: Record<string, KnownLicense> = {
  'CC-BY-SA-4.0': { short: 'CC BY-SA 4.0', url: 'https://creativecommons.org/licenses/by-sa/4.0/' },
  'CC-BY-4.0': { short: 'CC BY 4.0', url: 'https://creativecommons.org/licenses/by/4.0/' },
};

export interface LicenseAttributionProps {
  /** SPDX identifier, e.g. "CC-BY-SA-4.0". Null/undefined = a house template: renders nothing. */
  license?: string | null;
  /** Canonical URL of the original work. */
  sourceUrl?: string | null;
  /** Full human-readable credit line stored on the template. Shown in the panel variant. */
  sourceAttribution?: string | null;
  modifiedFromSource?: boolean | null;
  /** Short creator name for the badge. Defaults to the host of sourceUrl's owning org, else "the original author". */
  creator?: string;
  variant?: 'badge' | 'panel';
  className?: string;
}

/** Only https links are ever rendered as links. */
export function safeHttpsUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' ? u.toString() : null;
  } catch {
    return null;
  }
}

/** "https://github.com/JupiterOne/security-policy-templates/..." -> "JupiterOne". */
export function creatorFromSourceUrl(url: string | null | undefined): string | null {
  const safe = safeHttpsUrl(url);
  if (!safe) return null;
  const u = new URL(safe);
  if (u.hostname === 'github.com') return u.pathname.split('/').filter(Boolean)[0] ?? null;
  return u.hostname;
}

export function LicenseAttribution({
  license,
  sourceUrl,
  sourceAttribution,
  modifiedFromSource,
  creator,
  variant = 'badge',
  className,
}: LicenseAttributionProps) {
  const { t } = useTranslation();
  if (!license) return null;

  const tx = (key: Key, options?: Record<string, unknown>) =>
    t(key, { defaultValue: LICENSE_ATTRIBUTION_STRINGS[key], ...options });

  const known = LICENSES[license];
  const licenseName = known?.short ?? license;
  const licenseUrl = known?.url ?? null;
  const source = safeHttpsUrl(sourceUrl);
  const who = creator ?? creatorFromSourceUrl(sourceUrl) ?? tx('policies.license.sourceLabel');

  if (variant === 'badge') {
    const title = modifiedFromSource
      ? tx('policies.license.badgeTitle', { creator: who, license: licenseName })
      : tx('policies.license.badgeTitleUnmodified', { creator: who, license: licenseName });
    return (
      <span
        title={title}
        aria-label={title}
        className={cn(
          'inline-flex items-center gap-1 rounded border border-line bg-surface-sunken px-1.5 py-px',
          'text-[11px] font-medium leading-4 text-ink-secondary',
          className,
        )}
      >
        <span>{who}</span>
        <span aria-hidden>·</span>
        <span>{licenseName}</span>
        {modifiedFromSource ? (
          <>
            <span aria-hidden>·</span>
            <span>{tx('policies.license.adapted')}</span>
          </>
        ) : null}
      </span>
    );
  }

  return (
    <section
      aria-label={tx('policies.license.heading')}
      className={cn('rounded-xl border border-line bg-surface-sunken p-3 text-xs text-ink-secondary space-y-1.5', className)}
    >
      <h3 className="text-xs font-semibold text-ink">{tx('policies.license.heading')}</h3>
      {sourceAttribution ? (
        <p className="leading-relaxed">{sourceAttribution}</p>
      ) : (
        <>
          <p>{tx('policies.license.adaptedFrom', { source: who })}</p>
          {modifiedFromSource ? <p>{tx('policies.license.modified')}</p> : null}
          <p>{tx('policies.license.licensedUnder', { license: licenseName })}</p>
        </>
      )}
      <p className="leading-relaxed">{tx('policies.license.shareAlike', { license: licenseName })}</p>
      <p>{tx('policies.license.noWarranty')}</p>
      <p className="flex flex-wrap gap-x-4 gap-y-1">
        {source ? (
          <a className="underline" href={source} target="_blank" rel="noopener noreferrer">
            {tx('policies.license.viewSource')}
          </a>
        ) : null}
        {licenseUrl ? (
          <a className="underline" href={licenseUrl} target="_blank" rel="noopener noreferrer">
            {tx('policies.license.viewLicense')}
          </a>
        ) : null}
      </p>
    </section>
  );
}
